/**
 * useRealtimeConnections Hook
 *
 * Subscribes to Supabase Realtime for connections table
 * and triggers React Query refetch when connections or connection requests change.
 * Updates NetworkPage instantly when:
 * - New partners connect via QR code (status: 'accepted')
 * - New connection requests are received (status: 'pending')
 * - Connection requests are accepted/declined
 *
 * Uses the base useRealtimeSubscription hook for:
 * - Automatic reconnection with exponential backoff
 * - Connection status tracking
 * - Error handling with Sentry integration
 * - Debounced query invalidation
 */

import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtimeSubscription, RealtimeStatus } from './useRealtimeSubscription';

interface UseRealtimeConnectionsOptions {
  userId?: string;
  enabled?: boolean;
  onConnectionChange?: (connection: unknown) => void;
  onStatusChange?: (status: RealtimeStatus) => void;
}

interface UseRealtimeConnectionsResult {
  /** Current connection status */
  status: RealtimeStatus;
  /** Error if status is 'error' */
  error: Error | null;
  /** Manually unsubscribe */
  unsubscribe: () => void;
  /** Manually retry connection */
  retry: () => void;
  /** Number of reconnection attempts */
  reconnectAttempts: number;
}

export function useRealtimeConnections(
  options: UseRealtimeConnectionsOptions = {}
): UseRealtimeConnectionsResult {
  const { userId, enabled = true, onConnectionChange, onStatusChange } = options;
  const queryClient = useQueryClient();

  // Memoize the change handler to prevent re-subscriptions
  const handlePostgresChange = useCallback(
    (_table: string, payload: { eventType: string; new?: Record<string, unknown>; old?: Record<string, unknown> }) => {
      const data = (payload.new || payload.old) as { user_id?: string; connected_user_id?: string } | undefined;

      // Only process if this change affects the current user
      // (Supabase Realtime doesn't support OR filters, so we filter client-side)
      if (!data || (data.user_id !== userId && data.connected_user_id !== userId)) {
        return;
      }

      // Invalidate connections query to refetch
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/connections'] });

      // Invalidate pending connections query (for connection requests)
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/connections/pending'] });

      // Also invalidate dashboard stats (for connection count)
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/dashboard'] });

      // Drop a now-connected user out of the "Find New Partners" list in
      // real time. The suggested-partners endpoint already excludes existing
      // connections server-side, so refetching is enough — without this
      // invalidation the list would still show the partner until the page
      // is reloaded.
      queryClient.invalidateQueries({ queryKey: ['/api/partners/suggested'] });

      // Trade-category coverage for the user's network changes whenever a
      // connection is added/removed, so refresh that gauge too.
      queryClient.invalidateQueries({ queryKey: ['/api/workflows/partner-network-progress'] });

      // Call optional callback
      if (onConnectionChange) {
        onConnectionChange(payload.new || payload.old);
      }
    },
    [userId, queryClient, onConnectionChange]
  );

  // Memoize tables configuration
  // Note: We can't use OR filter in Supabase Realtime, so we subscribe to all
  // changes and filter client-side based on user_id/connected_user_id
  const tables = useMemo(
    () => [
      {
        table: 'connections',
        event: '*' as const,
        // No filter - we filter client-side for OR condition
      },
    ],
    []
  );

  // Use the base subscription hook
  const subscription = useRealtimeSubscription({
    channelName: `connections-${userId}`,
    enabled: enabled && !!userId,
    tables,
    onPostgresChange: handlePostgresChange,
    onStatusChange,
    debounceMs: 500, // Debounce rapid updates
  });

  return {
    status: subscription.status,
    error: subscription.error,
    unsubscribe: subscription.unsubscribe,
    retry: subscription.retry,
    reconnectAttempts: subscription.reconnectAttempts,
  };
}

export default useRealtimeConnections;
